/**
 * K4c.2 — the documents half of both deposit detail modals.
 *
 * WHY SHARED, AND WHY ONLY THIS MUCH
 * ----------------------------------
 * The two sides store their documents in different tables through different
 * endpoints — tenant deposits on `tenancy_documents`, landlord securities on
 * `unit_documents`. What they share is the SCREEN: a list that loads, may be
 * empty, may fail, and offers an upload, a download and a delete per row.
 *
 * So the storage stays outside. This component takes documents and three
 * callbacks and knows nothing about which table it is showing. That is the
 * whole abstraction: no adapter layer, no strategy object, no branch on a
 * `kind` prop. K4c.2's discovery said the real duplication is the list, and
 * only the list.
 *
 * It also does not decide WHICH documents belong here. The caller filters by
 * category before passing them in, because "what counts as a deposit document"
 * is a domain question with two different answers (three tenancy categories,
 * four unit ones) and neither belongs in a list renderer.
 *
 * NOT IN SCOPE, DELIBERATELY
 * --------------------------
 * No completeness. Nothing here says "contract missing" or "2 of 3", because
 * no rule in this system says a deposit needs a particular document — that
 * would depend on the deposit type, and inventing it would put a requirement
 * on screen that nobody agreed to. The empty state says nothing is stored,
 * not that something is missing.
 */
import React, { useRef, useState } from "react";

import { formatDateOnly } from "../../../utils/datetime";

const LABEL = "mb-1 block text-[12px] font-medium text-muted-foreground";
const FIELD =
  "w-full rounded-[8px] border border-border bg-background px-2.5 py-1.5 " +
  "text-[13px] text-foreground focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring disabled:opacity-50";

function ActionButton({ children, onClick, disabled, testId, tone }) {
  const base =
    "rounded-[7px] border px-2 py-1 text-[12px] font-medium transition-colors " +
    "disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none " +
    "focus-visible:ring-2 focus-visible:ring-ring";
  const tones = {
    default: "border-border bg-card text-foreground hover:bg-muted",
    danger:
      "border-border bg-transparent text-destructive hover:bg-destructive/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${base} ${tones[tone] || tones.default}`}
    >
      {children}
    </button>
  );
}

export default function DepositDocumentsList({
  documents,
  loading,
  error,
  onRetry,
  categoryOptions,
  categoryLabel,
  onUpload,
  onDownload,
  onDelete,
  uploading = false,
  deletingId = null,
  testIdPrefix,
}) {
  const [category, setCategory] = useState(categoryOptions[0]?.value || "");
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    // Cleared immediately so picking the same file twice still fires a change.
    e.target.value = "";
    if (!file) return;
    await onUpload(file, category);
  };

  const rows = Array.isArray(documents) ? documents : [];

  return (
    <section data-testid={`${testIdPrefix}-documents`} className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[12px] font-semibold text-foreground">
          {/* The count comes from the already-filtered list in hand — no
              aggregate, no second request, and absent while we do not know. */}
          {loading || error
            ? "Kautionsdokumente"
            : `Kautionsdokumente (${rows.length})`}
        </h3>
      </div>

      {/* Upload sits above the list and stays available even when the list
          failed to load: being unable to READ what is stored is no reason to
          block ADDING to it. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className={LABEL} htmlFor={`${testIdPrefix}-upload-category`}>
            Kategorie
          </label>
          <select
            id={`${testIdPrefix}-upload-category`}
            data-testid={`${testIdPrefix}-upload-category`}
            className={FIELD}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={uploading}
          >
            {categoryOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL} htmlFor={`${testIdPrefix}-upload-file`}>
            Datei
          </label>
          <input
            ref={fileRef}
            id={`${testIdPrefix}-upload-file`}
            data-testid={`${testIdPrefix}-upload-file`}
            type="file"
            onChange={handleFile}
            disabled={uploading}
            className="block w-full text-[12px] text-muted-foreground file:mr-2 file:rounded-[7px] file:border file:border-border file:bg-card file:px-2 file:py-1 file:text-[12px] file:font-medium file:text-foreground hover:file:bg-muted disabled:opacity-50"
          />
        </div>
      </div>
      {uploading ? (
        <p
          data-testid={`${testIdPrefix}-uploading`}
          className="text-[12px] text-muted-foreground"
        >
          Dokument wird hochgeladen …
        </p>
      ) : null}

      {loading ? (
        <p
          data-testid={`${testIdPrefix}-documents-loading`}
          className="text-[12px] text-muted-foreground"
        >
          Dokumente werden geladen …
        </p>
      ) : error ? (
        /* Scoped to this section on purpose. The facts above come from the row
           the reader clicked and are already on screen; a failed document
           fetch must not blank them out. */
        <div
          data-testid={`${testIdPrefix}-documents-error`}
          className="flex flex-wrap items-center gap-2 rounded-[8px] border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-foreground"
        >
          <span>{error}</span>
          {onRetry ? (
            <ActionButton
              onClick={onRetry}
              testId={`${testIdPrefix}-documents-retry`}
            >
              Erneut versuchen
            </ActionButton>
          ) : null}
        </div>
      ) : rows.length === 0 ? (
        <p
          data-testid={`${testIdPrefix}-documents-empty`}
          className="text-[12px] text-muted-foreground"
        >
          {/* "Vorhanden", not "fehlt": this view reports what is stored, and
              says nothing about what ought to be. */}
          Keine Kautionsdokumente vorhanden.
        </p>
      ) : (
        <ul className="space-y-2" data-testid={`${testIdPrefix}-documents-list`}>
          {rows.map((doc) => (
            <li
              key={doc.id}
              data-testid={`${testIdPrefix}-document-${doc.id}`}
              className="flex flex-wrap items-start justify-between gap-2 rounded-[8px] border border-border px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-foreground">
                  {categoryLabel(doc.category)}
                </p>
                {/* `break-all` rather than truncate: a file name is how an
                    operator recognises the document, and a cut-off name on a
                    375px screen is the one thing they cannot recover. */}
                <p
                  className="break-all text-[12px] text-muted-foreground"
                  title={doc.file_name || ""}
                >
                  {doc.file_name || "—"}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {formatDateOnly(doc.created_at) || "—"}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <ActionButton
                  onClick={() => onDownload(doc)}
                  testId={`${testIdPrefix}-download-${doc.id}`}
                >
                  Herunterladen
                </ActionButton>
                <ActionButton
                  tone="danger"
                  onClick={() => onDelete(doc)}
                  disabled={deletingId === doc.id}
                  testId={`${testIdPrefix}-delete-${doc.id}`}
                >
                  {deletingId === doc.id ? "Löscht …" : "Löschen"}
                </ActionButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
